// src/components/audio/FlaggedRowActionsCell.js
//
// Bundles the per-row audio actions for the ACTIONS column of flagged-rows
// tables. Replaces the inline FlaggedRowAudioButton in section components.
//
// LAYOUT
// ======
//   [▶ + confidence + 🌐 retranscribe]  [📝 N]  [✓ ! ×]
//   └──── existing FlaggedRowAudioButton ─────┘  └─Notes─┘  └─Disposition─┘
//
// Renders nothing if no audio can be resolved for this row — same graceful
// degradation as FlaggedRowAudioButton, so non-audio flagged rows (numeric
// anomalies, gate-only flags on text columns, etc.) get an empty cell rather
// than a broken control.

import React, { useMemo } from 'react';
import FlaggedRowAudioButton, {
  resolveAudioForFlaggedRow,
} from './FlaggedRowAudioButton';
import FlaggedRowNotesButton from './FlaggedRowNotesButton';
import FlaggedRowDispositionToggle from './FlaggedRowDispositionToggle';
import { resolveAudioPath } from './AudioPlayer';

const FlaggedRowActionsCell = ({
  // Same as FlaggedRowAudioButton
  variable,
  rowIndex,
  primaryData,
  audioSettings,
  tauriApi,
  isDarkMode,
  transcriptsMeta = null,
  projectId = null,
  userInfo = null,
  audioNotesDoc = null,
  onMetaUpdated = null,
  onAudioNotesUpdated = null,
  // New for Ship 3 — submission identifier for keying notes & dispositions
  submissionId = null,
  // Optional navigation callback — if provided, Notes popover shows
  // "Open in Audio Audit" link
  onOpenAudit = null,
}) => {
  const resolved = useMemo(
    () => resolveAudioForFlaggedRow(variable, rowIndex, primaryData),
    [variable, rowIndex, primaryData]
  );

  if (!resolved) return null;
  if (!audioSettings?.media_dir) return null;
  if (!tauriApi?.invoke) return null;

  const audioPath = resolveAudioPath(audioSettings.media_dir, resolved.filename);

  // Fall back to a synthetic submission ID when respondent_id wasn't configured.
  // Keeps notes/dispositions stable across refreshes — keyed by row index.
  const effectiveSubmissionId = submissionId || `row_${rowIndex}`;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      gap: '8px',
      flexShrink: 0,
    }}>
      <FlaggedRowAudioButton
        variable={variable}
        rowIndex={rowIndex}
        primaryData={primaryData}
        audioSettings={audioSettings}
        tauriApi={tauriApi}
        isDarkMode={isDarkMode}
        transcriptsMeta={transcriptsMeta}
        projectId={projectId}
        userInfo={userInfo}
        audioNotesDoc={audioNotesDoc}
        onMetaUpdated={onMetaUpdated}
        onAudioNotesUpdated={onAudioNotesUpdated}
      />
      <FlaggedRowNotesButton
        audioPath={audioPath}
        audioColumn={resolved.audioColumn}
        filename={resolved.filename}
        submissionId={effectiveSubmissionId}
        audioNotesDoc={audioNotesDoc}
        onAudioNotesUpdated={onAudioNotesUpdated}
        projectId={projectId}
        userInfo={userInfo}
        isDarkMode={isDarkMode}
        onOpenAudit={onOpenAudit}
      />
      <FlaggedRowDispositionToggle
        submissionId={effectiveSubmissionId}
        audioColumn={resolved.audioColumn}
        audioNotesDoc={audioNotesDoc}
        onAudioNotesUpdated={onAudioNotesUpdated}
        projectId={projectId}
        isDarkMode={isDarkMode}
      />
    </span>
  );
};

export default FlaggedRowActionsCell;
